import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { Radio } from '../../../../../../widgets/radio';
import { AdvancedSelect } from '../../../../../../widgets/select';
import React from 'react';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import ComposerUploadComponent from '../components/composerUploadComponent';
import Stack from '../../../../../../widgets/stack/stack';
import { ValidationBox } from '../../../../../../widgets/validation/validationBox';
import { NotificationBanner } from '../../../../../../widgets/notificationBanner';
import { selectionSurface } from './composerSelectionLayout';

export enum ComposerScreenshotType {
    Object,
    Material,
    Upload
}
export interface ComposerScreenshotOutput {
    type: ComposerScreenshotType;
    attributeFilter: string;
    optionsFilter: string;
    material?: string;
    upload?: {
        url: string;
        key: string;
    };
}
interface ComposerScreenshotAttributeDialogProps {
    materialsList: BABYLON.Material[];
    onConfirm: (attributes: ComposerScreenshotOutput) => void;
}

const TypeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const TypeTile = styled.div<{ $selected: boolean }>`
    ${selectionSurface}
    padding: 12px;
    cursor: pointer;
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const FiltersHint = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${(props) => props.theme.app.primary};
`;

const ComposerScreenshotAttributeDialog: React.FC<ComposerScreenshotAttributeDialogProps> = ({ materialsList, onConfirm }) => {
    const validationContext = useValidationContext();
    const [type, setType] = React.useState<ComposerScreenshotType>(ComposerScreenshotType.Object);
    const [attributeFilter, setAttributeFilter] = React.useState<string>('');
    const [optionsFilter, setOptionsFilter] = React.useState<string>('');

    const [material, setMaterial] = React.useState<string | undefined>(undefined);
    const [upload, setUpload] = React.useState<ComposerScreenshotOutput['upload']>();

    const typeOptions = React.useMemo(
        () => [
            { value: ComposerScreenshotType.Object, label: T._('Object', 'Admin'), caption: T._('Capture the current 3D scene', 'Admin') },
            { value: ComposerScreenshotType.Material, label: T._('Material', 'Admin'), caption: T._('Capture a preview of a scene material', 'Admin') },
            { value: ComposerScreenshotType.Upload, label: T._('Upload', 'Admin'), caption: T._('Use an image from your computer', 'Admin') }
        ],
        []
    );

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const screenshot: ComposerScreenshotOutput = {
            type,
            attributeFilter,
            optionsFilter,
            material,
            upload
        };
        onConfirm(screenshot);
    };

    return (
        <Dialog size='small' title={T._('Apply Screenshot to Multiple Options', 'Admin')} buttons={[{ label: T._('Apply', 'Admin'), onClick: handleConfirm }]}>
            <Stack>
                <FormControl label={T._('Screenshot type', 'Admin')} tooltipLabelHtml={T._('Select the type of screenshot to apply', 'Admin')}>
                    <TypeGrid role='radiogroup' aria-label={T._('Screenshot type', 'Admin')}>
                        {typeOptions.map((option) => (
                            <TypeTile key={option.value} $selected={type === option.value} onClick={() => setType(option.value)}>
                                <Radio
                                    name='screenshotType'
                                    label={option.label}
                                    caption={option.caption}
                                    value={option.value}
                                    checked={type === option.value}
                                    onSelect={setType}
                                />
                            </TypeTile>
                        ))}
                    </TypeGrid>
                </FormControl>
                {type === ComposerScreenshotType.Material && (
                    <FormControl label={T._('Material', 'Admin')} tooltipLabelHtml={T._('Screenshot will be fetched from the material', 'Admin')}>
                        <AdvancedSelect
                            value={material}
                            onChange={(e) => setMaterial(e)}
                            validationKey='composerScreenshotMaterial'
                            validationLabel={T._('Material', 'Admin')}
                            validate={(value) => validateRequiredTrimmed(value?.toString())}
                            validationMessage={T._('Material is required.', 'Admin')}
                        >
                            <option value=''>{T._('Select a material', 'Admin')}</option>
                            {materialsList.map(({ id, name }) => (
                                <option key={id} value={id}>
                                    {name}
                                </option>
                            ))}
                        </AdvancedSelect>
                    </FormControl>
                )}
                {type === ComposerScreenshotType.Upload && (
                    <FormControl label={T._('Upload', 'Admin')} tooltipLabelHtml={T._('Screenshot will be uploaded from your computer', 'Admin')}>
                        <ValidationBox
                            fullWidth
                            validationKey='composerScreenshotUpload'
                            validationLabel={T._('Upload', 'Admin')}
                            validationValue={upload?.key}
                            validate={(value) => validateRequiredTrimmed(value)}
                            validationMessage={T._('Upload is required.', 'Admin')}
                        >
                            <ComposerUploadComponent
                                onImagePick={(url, key) => {
                                    setUpload(url && key ? { url, key } : undefined);
                                }}
                            />
                        </ValidationBox>
                    </FormControl>
                )}
                <Stack $gap='8px'>
                    <FiltersGrid>
                        <FormControl
                            label={T._('Attributes filter (optional)', 'Admin')}
                            tooltipLabelHtml={T._('Only attributes whose name matches the filter are updated', 'Admin')}
                        >
                            <Input
                                placeholder={T._('You can use wildcards like *', 'Admin')}
                                type='text'
                                value={attributeFilter}
                                onChange={(e) => setAttributeFilter(e.target.value)}
                            />
                        </FormControl>
                        <FormControl
                            label={T._('Options filter (optional)', 'Admin')}
                            tooltipLabelHtml={T._('Only options whose name matches the filter are updated', 'Admin')}
                        >
                            <Input
                                placeholder={T._('You can use wildcards like *', 'Admin')}
                                type='text'
                                value={optionsFilter}
                                onChange={(e) => setOptionsFilter(e.target.value)}
                            />
                        </FormControl>
                    </FiltersGrid>
                    <FiltersHint>{T._('Leave both filters empty to apply the screenshot to every option.', 'Admin')}</FiltersHint>
                </Stack>
                <NotificationBanner type='WARNING' variant='slim' content={T._('Existing preview images of the matching options will be replaced.', 'Admin')} />
            </Stack>
        </Dialog>
    );
};

const ComposerScreenshotAttributeDialogWithValidation: React.FC<ComposerScreenshotAttributeDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerScreenshotAttributeDialog {...props} />
    </ValidationStateProvider>
);

export default ComposerScreenshotAttributeDialogWithValidation;
